import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  InputSearch,
  TableData,
  Pagination,
  DateRangeFilter,
  UniversalFilter,
  ActionDropdown,
  ActionDropdownAction,
  ColumnSettings,
} from '@/components';
import { useTableColumns } from '@/hooks/useTableColumns';
import {
  Column,
  Issue,
  IssueStatus,
  IssueSeverity,
  Oem,
  Model,
} from '@/types/common';
import { getListOem } from '@/services/api/oems';
import { getListModel } from '@/services/api/models';
import { ReportIssueModal, IssueFormData } from './components/ReportIssueModal';
import { EditIssueModal } from './components/EditIssueModal';
import { CreateMaintenanceModal } from './components/CreateMaintenanceModal';
import {
  createIssue,
  CreateIssueRequest,
  getListIssues,
  markIssueAsClose,
} from '@/services/api/issues';
import { getUserEmail, extractApiErrorMessage } from '@/utils';
import {
  showSuccessToast,
  showErrorToast,
} from '@/components/toast/globalToast';

const ISSUE_TYPE_MAP: Record<string, string> = {
  ACCIDENT_DAMAGE: 'Kerusakan akibat Kecelakaan',
  USAGE_DAMAGE: 'Kerusakan akibat Penggunaan',
  LOST_UNIT: 'Kehilangan Unit',
};

const ISSUE_STATUS_MAP: Record<string, string> = {
  SUBMITTED: 'Submitted',
  IN_PROGRESS: 'In Progress',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
};

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface IssueFilters {
  statuses?: IssueStatus[];
  severities?: IssueSeverity[];
  oemIDs?: string[];
  modelIDs?: string[];
  issueTypes?: string[];
}

const IssuesPage: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null
  );

  const [activeFilters, setActiveFilters] = useState<IssueFilters>({});
  const [dateRange, setDateRange] = useState<DateRange>({
    from: null,
    to: null,
  });

  const [oems, setOems] = useState<Oem[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [oemRes, modelRes] = await Promise.all([
          getListOem({ page: '1', size: '100' }),
          getListModel({ page: '1', size: '100' }),
        ]);
        setOems(oemRes.data || []);
        setModels(modelRes.data || []);
      } catch (error) {
        console.error('Error loading reference data:', error);
      }
    };
    loadReferenceData();
  }, []);

  const [issues, setIssues] = useState<Issue[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [actionDropdownOpen, setActionDropdownOpen] = useState<
    string | number | null
  >(null);
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false);

  const fetchIssues = useCallback(
    async (
      page: number,
      search: string,
      filters: IssueFilters,
      size: number,
      range: DateRange
    ) => {
      setIsLoading(true);
      try {
        const response = await getListIssues({
          page: String(page),
          size: String(size),
          search: search || undefined,
          statuses: filters.statuses?.join(',') || undefined,
          severities: filters.severities?.join(',') || undefined,
          oemIDs: filters.oemIDs?.join(',') || undefined,
          modelIDs: filters.modelIDs?.join(',') || undefined,
          issueTypes: filters.issueTypes?.join(',') || undefined,
          startDate: range?.from
            ? `${range.from.getFullYear()}-${String(range.from.getMonth() + 1).padStart(2, '0')}-${String(range.from.getDate()).padStart(2, '0')}`
            : undefined,
          endDate: range?.to
            ? `${range.to.getFullYear()}-${String(range.to.getMonth() + 1).padStart(2, '0')}-${String(range.to.getDate()).padStart(2, '0')}`
            : undefined,
        });

        if (response?.data) {
          setIssues(response.data);
          setTotal(response.pagination?.total || 0);
        } else {
          setIssues([]);
          setTotal(0);
        }
      } catch (error) {
        console.error('Failed to fetch issues:', error);
        setIssues([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchIssues(currentPage, searchTerm, activeFilters, pageSize, dateRange);
  }, [
    fetchIssues,
    currentPage,
    searchTerm,
    activeFilters,
    pageSize,
    dateRange,
  ]);

  const filterConfigs = useMemo(
    () => [
      {
        key: 'oemIDs',
        title: 'OEM Name',
        icon: 'ri-building-line',
        multiple: true,
        dataSource: oems,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'modelIDs',
        title: 'Models Name',
        icon: 'ri-car-line',
        multiple: true,
        dataSource: models,
        labelFormatter: (model: Model) => `${model?.oemName} - ${model?.name}`,
        valueField: 'ID',
      },
      {
        key: 'issueTypes',
        title: 'Issue Type',
        icon: 'ri-error-warning-line',
        multiple: true,
        dataSource: [
          { id: 'ACCIDENT_DAMAGE', name: 'Kerusakan akibat Kecelakaan' },
          { id: 'USAGE_DAMAGE', name: 'Kerusakan akibat Penggunaan' },
          { id: 'LOST_UNIT', name: 'Kehilangan Unit' },
        ],
        labelField: 'name',
        valueField: 'id',
      },
      {
        key: 'severities',
        title: 'Severity',
        icon: 'ri-alert-line',
        multiple: true,
        dataSource: [
          { id: IssueSeverity.LOW, name: 'Low' },
          { id: IssueSeverity.MEDIUM, name: 'Medium' },
          { id: IssueSeverity.HIGH, name: 'High' },
          { id: IssueSeverity.CRITICAL, name: 'Critical' },
        ],
        labelField: 'name',
        valueField: 'id',
      },
      {
        key: 'statuses',
        title: 'Status',
        icon: 'ri-flag-line',
        multiple: true,
        dataSource: [
          { id: IssueStatus.SUBMITTED, name: 'Submitted' },
          { id: IssueStatus.IN_PROGRESS, name: 'In Progress' },
          { id: IssueStatus.RESOLVED, name: 'Resolved' },
          { id: IssueStatus.CLOSED, name: 'Closed' },
        ],
        labelField: 'name',
        valueField: 'id',
      },
    ],
    [oems, models]
  );

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  }, []);

  const handleSort = (field: string, direction: 'asc' | 'desc' | null) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handleDateRangeChange = useCallback((range: DateRange) => {
    setDateRange(range);
    setCurrentPage(1);
  }, []);

  const handleFiltersApply = useCallback((filters: IssueFilters) => {
    setActionDropdownOpen(null);
    setActiveFilters(filters);
    setCurrentPage(1);
  }, []);

  const handleFiltersClear = useCallback(() => {
    setActionDropdownOpen(null);
    setActiveFilters({});
    setCurrentPage(1);
  }, []);

  const handleToggleDropdown = useCallback((uniqueId: string | number) => {
    setActionDropdownOpen((prev) => (prev === uniqueId ? null : uniqueId));
  }, []);

  const handleEditClick = useCallback((record: Issue) => {
    setSelectedIssue(record);
    setIsEditModalOpen(true);
    setActionDropdownOpen(null);
  }, []);

  const handleMarkAsClose = useCallback(
    async (issue: Issue) => {
      if (!issue.ID) return;

      try {
        const userEmail = getUserEmail();

        await markIssueAsClose(issue.ID, {
          updatedBy: userEmail,
        });

        showSuccessToast('Success', 'Issue marked as closed');
        setActionDropdownOpen(null);

        fetchIssues(
          currentPage,
          searchTerm,
          activeFilters,
          pageSize,
          dateRange
        );
      } catch (error) {
        const errorMessage = extractApiErrorMessage(
          error,
          'Failed to mark issue as closed'
        );
        showErrorToast('Error', errorMessage);
      }
    },
    [currentPage, searchTerm, activeFilters, pageSize, dateRange, fetchIssues]
  );
  useEffect(() => {
    const handleClickOutside = () => {
      setActionDropdownOpen(null);
    };

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActionDropdownOpen(null);
      }
    };

    if (actionDropdownOpen !== null) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [actionDropdownOpen]);

  const sortedData = useMemo(() => {
    if (!sortField || !sortDirection) return issues;

    return [...issues].sort((a: any, b: any) => {
      if (sortField === 'severity') {
        const severityOrder: Record<string, number> = {
          LOW: 1,
          MEDIUM: 2,
          HIGH: 3,
          CRITICAL: 4,
        };
        const rankA = severityOrder[a[sortField]] || 0;
        const rankB = severityOrder[b[sortField]] || 0;
        return sortDirection === 'asc' ? rankA - rankB : rankB - rankA;
      }

      const aValue = a[sortField];
      const bValue = b[sortField];

      const comparison = String(aValue || '').localeCompare(
        String(bValue || '')
      );
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [issues, sortField, sortDirection]);

  const handleCreateIssue = async (data: IssueFormData) => {
    try {
      const userEmail = getUserEmail();
      if (!userEmail) throw new Error('User email not found');

      const issueTypeKey =
        Object.keys(ISSUE_TYPE_MAP).find(
          (key) => ISSUE_TYPE_MAP[key] === data.issueType
        ) || data.issueType;

      const payload: CreateIssueRequest = {
        issueType: issueTypeKey,
        handoverID: data.handoverID,
        driverID: data.driverID ? Number(data.driverID) : undefined,
        driverCode: data.driverCode,
        driverName: data.driverName,
        clientID: data.clientID,
        clientName: data.clientName,
        incidentDate: data.incidentDate,
        severity: data.severity,
        estimatedCost: data.estimatedCost,
        costResponsibility: data.costBearer || 'MITRA',
        imageURL: data.imageURL || '',
        notes: data.notes,
        createdBy: userEmail,
        oemID: data.oemID,
      };

      await createIssue(payload);

      setIsReportModalOpen(false);
      fetchIssues(currentPage, searchTerm, activeFilters, pageSize, dateRange);
      showSuccessToast('Success', 'Issue created successfully');
    } catch (error) {
      const msg = extractApiErrorMessage(error, 'Failed to create issue');
      showErrorToast('Error', msg);
      throw error;
    }
  };

  const columns: Column<Issue>[] = [
    {
      key: 'code',
      title: 'IssuesID',
      sortable: true,
      render: (value: string) => (
        <span className="font-medium text-[14px]">{value}</span>
      ),
    },
    {
      key: 'linkedMaintenanceCode',
      title: 'MaintenanceID',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px]">{value || '-'}</span>
      ),
    },
    {
      key: 'vehicle',
      title: 'Vehicle',
      render: (_, record) => (
        <div className="text-[14px]">
          <div className="font-medium">{record.vehiclePlatNomor}</div>
          <div className="text-gray-500 text-[11px]">
            {record.vehicleOemName && record.vehicleModelName
              ? `${record.vehicleOemName} - ${record.vehicleModelName}`
              : record.vehicleModelName || record.vehicleOemName || '-'}
          </div>
        </div>
      ),
    },
    {
      key: 'mitra',
      title: 'Mitra',
      render: (_, record) => {
        const name = record.mitraName || record.driverName || '-';
        const code = record.mitraCode || record.driverCode || '-';
        const driverID = record.driverID;

        return (
          <div
            className={`text-[14px] ${driverID
              ? 'text-underline text-blue-600 cursor-pointer hover:text-blue-800'
              : 'text-gray-900 cursor-default'
              }`}
            onClick={() => {
              if (driverID) {
                const backOfficeUrl =
                  process.env.REACT_APP_BACK_OFFICE_URL ||
                  'https://back-office.dashelectric.co';

                window.open(`${backOfficeUrl}/driver/${driverID}`, '_blank');
              }
            }}
          >
            <div>{name}</div>
            <div className="text-gray-500 text-[11px]">{code}</div>
          </div>
        );
      },
    },
    {
      key: 'issueType',
      title: 'Issue Type',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px]">{ISSUE_TYPE_MAP[value] || value}</span>
      ),
    },
    {
      key: 'severity',
      title: 'Severity',
      align: 'center',
      sortable: true,
      render: (value: IssueSeverity) => (
        <div className="text-center">
          {value ? (
            <span className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700">
              <span
                className={`h-2 w-2 rounded-full ${value === IssueSeverity.LOW
                  ? 'bg-green-500'
                  : value === IssueSeverity.MEDIUM
                    ? 'bg-blue-500'
                    : value === IssueSeverity.HIGH
                      ? 'bg-orange-500'
                      : value === IssueSeverity.CRITICAL
                        ? 'bg-red-500'
                        : 'bg-gray-400'
                  }`}
              />
              {value}
            </span>
          ) : (
            <span className="text-[14px]">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      align: 'center',
      sortable: true,
      render: (value: IssueStatus) => (
        <div className="text-center">
          {value ? (
            <span
              className={`inline-block px-2 py-1 text-xs rounded-full font-medium ${value === IssueStatus.SUBMITTED
                ? 'bg-blue-100 text-blue-800'
                : value === IssueStatus.IN_PROGRESS
                  ? 'bg-yellow-100 text-yellow-800'
                  : value === IssueStatus.RESOLVED
                    ? 'bg-green-100 text-green-800'
                    : value === IssueStatus.CLOSED
                      ? 'bg-gray-100 text-gray-800'
                      : 'bg-gray-100 text-gray-800'
                }`}
            >
              {ISSUE_STATUS_MAP[value]}
            </span>
          ) : (
            <span className="text-[14px]">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'incidentDate',
      title: 'Tanggal Kejadian',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px]">
          {new Date(value).toLocaleDateString('en-GB')}
        </span>
      ),
    },
    {
      key: 'createdBy',
      title: 'Created By',
      sortable: true,
      render: (_: string, record: Issue) => (
        <span className="text-[14px]">{record.createdBy || '-'}</span>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 'w-20',
      align: 'center',
      render: (_: any, record: Issue) => {
        const uniqueId = record.ID;

        const actions: ActionDropdownAction[] = [];

        if (
          (record.issueType === 'ACCIDENT_DAMAGE' ||
            record.issueType === 'USAGE_DAMAGE') &&
          !record.linkedMaintenanceID &&
          record.status !== IssueStatus.IN_PROGRESS
        ) {
        }

        if (
          record.status === IssueStatus.SUBMITTED ||
          record.status === IssueStatus.IN_PROGRESS
        ) {
          actions.push({
            key: 'close',
            label: 'Mark as Close',
            icon: 'ri-close-line',
            onClick: () => handleMarkAsClose(record),
            separateAfter: true,
          });
        }
        if (record.status !== IssueStatus.CLOSED) {
          actions.push({
            key: 'edit',
            label: 'Edit',
            icon: 'ri-pencil-line',
            onClick: () => handleEditClick(record),
            separateAfter: true,
          });

          // actions.push({
          //   key: 'detail',
          //   label: 'Detail',
          //   icon: 'ri-eye-line',
          //   onClick: () => setActionDropdownOpen(null),
          //   separateAfter: true,
          // });

          return (
            <ActionDropdown
              record={record}
              isOpen={actionDropdownOpen === uniqueId}
              onToggle={() => handleToggleDropdown(uniqueId)}
              actions={actions}
              ariaLabel={`Actions for issue ${record.code}`}
            />
          );
        }
      },
    },
  ];

  // Column customization (show/hide + reorder), persisted per browser.
  const { visibleColumns, columnSettingsProps } = useTableColumns<Issue>({
    storageKey: 'issues',
    columns,
  });

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[24px] font-semibold text-gray-900 mb-1">
              Issues
            </h1>
            <p className="text-[14px] text-gray-600">
              Overview and management of all fleet issues.
            </p>
          </div>
          {/* <Button
            variant="primary"
            size="small"
            onClick={() => setIsReportModalOpen(true)}
          >
            <i className="ri-add-line text-[12px] mr-1"></i> Report Issues
          </Button> */}
        </div>
      </div>

      <div className="rounded-lg p-2 mb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center">
            <DateRangeFilter
              onDateRangeChange={handleDateRangeChange}
              initialRange={dateRange}
            />
          </div>

          <div className="flex items-center gap-2">
            <InputSearch
              placeholder="Search…"
              value={searchInput}
              onChange={setSearchInput}
              onSearch={handleSearch}
              onClear={() => handleSearch('')}
              debounceMs={300}
              size="small"
            />

            <UniversalFilter
              filters={filterConfigs}
              currentFilters={activeFilters}
              onApply={handleFiltersApply}
              onClear={handleFiltersClear}
            />

            <ColumnSettings {...columnSettingsProps} />
          </div>
        </div>
      </div>

      {searchTerm && !isLoading && (
        <div className="mb-3 px-2">
          <p className="text-sm text-gray-600">
            Found <span className="font-semibold">{total}</span> result
            {total !== 1 ? 's' : ''} for{' '}
            <span className="font-semibold">'{searchTerm}'</span>
          </p>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="relative">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2 text-gray-600">Loading issues...</span>
            </div>
          ) : (
            <TableData
              data={sortedData}
              columns={visibleColumns}
              sorting={{
                field: sortField,
                direction: sortDirection,
                onSort: handleSort,
              }}
              size="small"
              className="min-w-full"
            />
          )}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={total}
        itemsPerPage={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setCurrentPage(1);
        }}
      />
      <ReportIssueModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        onSubmit={handleCreateIssue}
      />
      <EditIssueModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedIssue(null);
        }}
        issue={selectedIssue}
        onSuccess={() => {
          fetchIssues(
            currentPage,
            searchTerm,
            activeFilters,
            pageSize,
            dateRange
          );
        }}
      />
      <CreateMaintenanceModal
        isOpen={isMaintenanceModalOpen}
        onClose={() => {
          setIsMaintenanceModalOpen(false);
          setSelectedIssue(null);
        }}
        issue={selectedIssue}
        onSuccess={(maintenanceCode?: string) => {
          if (maintenanceCode && selectedIssue) {
            setIssues((prevIssues) =>
              prevIssues.map((iss) =>
                iss.ID === selectedIssue.ID
                  ? {
                    ...iss,
                    linkedMaintenanceCode: maintenanceCode,
                    linkedMaintenanceID: 'created',
                  }
                  : iss
              )
            );
          }
          fetchIssues(
            currentPage,
            searchTerm,
            activeFilters,
            pageSize,
            dateRange
          );
        }}
      />
      <CreateMaintenanceModal
        isOpen={isMaintenanceModalOpen}
        onClose={() => {
          setIsMaintenanceModalOpen(false);
          setSelectedIssue(null);
        }}
        issue={selectedIssue}
        onSuccess={(maintenanceCode?: string) => {
          if (maintenanceCode && selectedIssue) {
            setIssues((prevIssues) =>
              prevIssues.map((iss) =>
                iss.ID === selectedIssue.ID
                  ? {
                    ...iss,
                    linkedMaintenanceCode: maintenanceCode,
                    linkedMaintenanceID: 'created',
                  }
                  : iss
              )
            );
          }
          fetchIssues(
            currentPage,
            searchTerm,
            activeFilters,
            pageSize,
            dateRange
          );
        }}
      />
    </div>
  );
};

export default IssuesPage;
